/**
 * The portal's configuration, read at start from `config.json` next to `index.html`: written at
 * deploy from the stacks' outputs (`scripts/write-config.ts`), so one build serves any environment.
 */
export interface PortalConfig {
  /** The data API's GraphQL endpoint: the app stack's `ApiUrl`. */
  readonly apiUrl: string;
  /** The Cognito managed-login domain: the identity stack's `AuthDomain`. */
  readonly authDomain?: string;
  /** The admin portal's app client: the identity stack's `AdminClientId`. */
  readonly clientId?: string;
  /** Dev server only: a local data API's development token, used instead of signing in. */
  readonly devToken?: string;
  /**
   * The address the agency's Gmail forwards emailed sign-in codes to: the app stack's
   * `SignInMailAddress` (docs/credentials.md#emailed-codes). Absent where emailed codes are not set
   * up.
   */
  readonly signInMail?: string;
}

/** An address such as `signin@quote-mail.roundblock.io`. */
const SIGN_IN_MAIL = /^[^@\s]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;

export class ConfigError extends Error {
  override readonly name = "ConfigError";
}

/** Checks a parsed `config.json`. A dev token counts only on the dev server. */
export function parseConfig(value: unknown, dev: boolean): PortalConfig {
  if (typeof value !== "object" || value === null)
    throw new ConfigError("config.json is not an object");
  const record = value as Record<string, unknown>;
  const text = (name: string) => {
    const field = record[name];
    return typeof field === "string" && field.trim() !== "" ? field.trim() : undefined;
  };
  const apiUrl = text("apiUrl");
  if (!apiUrl) throw new ConfigError("config.json has no apiUrl");
  const signInMail = text("signInMail");
  if (signInMail !== undefined && !SIGN_IN_MAIL.test(signInMail)) {
    throw new ConfigError("signInMail must be an email address");
  }
  const mail = signInMail ? { signInMail: signInMail.toLowerCase() } : {};
  const devToken = dev ? text("devToken") : undefined;
  if (devToken) return { apiUrl, devToken, ...mail };
  const authDomain = text("authDomain");
  const clientId = text("clientId");
  if (!authDomain || !clientId) {
    throw new ConfigError("config.json needs authDomain and clientId to sign in");
  }
  if (!/^https:\/\//.test(authDomain)) throw new ConfigError("authDomain must be an https URL");
  return { apiUrl, authDomain: authDomain.replace(/\/+$/, ""), clientId, ...mail };
}

/** Fetches and checks `config.json`; never cached, since it changes with the environment. */
export async function loadConfig(fetchImpl: typeof fetch = fetch): Promise<PortalConfig> {
  const response = await fetchImpl("/config.json", { cache: "no-store" });
  if (!response.ok) throw new ConfigError(`config.json: HTTP ${response.status}`);
  return parseConfig(await response.json(), import.meta.env.DEV);
}
